import {colorSwatch} from '../utils/colorSwatch.js';
import {MenuPanels} from '../../models/UIModel.js';
import React, {useState, useEffect, useMemo, useContext} from 'react';
import {MenuButton} from '../components/menu-button.js';

import {
    Column,
    Container,
    Image,
    MaterialContext,
    Panel,
    Panel9Slice,
    PanelProps,
    ProgressBar,
    Row,
    Text,
} from '@wonderlandengine/react-ui/components';
import {Align, FlexDirection, Justify, PositionType} from '@wonderlandengine/react-ui';
import {goToMenu} from '../utils/goToMenu.js';
import {
    RotBMaterialContext,
    RotBMaterialContextValue,
    useRotBMaterialContext,
} from '../utils/rotb-material-context.js';
import {Window} from '../components/window.js';
import {Statistics} from '../../components/statistics.js';
import {TransactionalNames} from '../../enums/TransactionalNames.js';
import {WindowHeader} from '../components/window-header.js';
import {UiTexturesManager} from '../components/ui-textures-manager.js';
import {StoreInventoryItems} from '../../enums/StoreInventoryItems.js';
import {StorePowerupsData} from '../../components/store-data/powerups/StorePowerupsData.js';
import {Texture} from '@wonderlandengine/api';
import {Localization} from '../../utils/Localization.js';
import {insertNewlines, parseColor, resizeLabelToFit} from '../utils/utils.js';
import {goToPreviousMenu} from '../utils/goToPreviousMenu.js';
import {FlatOpaqueTextured} from '../../types/materials.js';
import {MissionManager} from '../../components/mission-manager.js';
import {goToRewards} from '../utils/goToRewards.js';
import {goToSkipMissions} from '../utils/goToSkipMissions.js';
import {Sounds} from '../../enums/Sounds.js';

export const MissionDetails = (props: {
    missionId: number;
    condense?: boolean;
    width?: number;
}) => {
    const mats = useContext(MaterialContext);
    const rotbMats = useContext(RotBMaterialContext);
    const material = mats.panelMaterialTextured;
    const backgroundMaterial = useMemo(() => {
        const m = material!.clone() as FlatOpaqueTextured;
        m.setColor(parseColor(colorSwatch.MissionRequirementsBG));
        m.setFlatTexture(rotbMats?.buttonSimple);
        return m;
    }, []);

    const [mission] = useState(
        MissionManager.instance.getMissionInstructions(props.missionId)
    );

    return (
        <Panel9Slice
            material={backgroundMaterial}
            paddingLeft={8}
            borderSize={16}
            borderTextureSize={0.3}
            backgroundColor={colorSwatch.MissionRequirementsBG}
            width={props.width ?? 440}
            height={props.condense ? 120 : 170}
        >
            <Row height="100%">
                <Container
                    margin={15}
                    justifyContent={Justify.Center}
                    alignItems={Align.Center}
                >
                    <Panel
                        width={50}
                        height={50}
                        justifyContent={Justify.Center}
                        alignItems={Align.Center}
                        rounding={0}
                        backgroundColor="#FFFFFF"
                    >
                        <Panel
                            justifyContent={Justify.Center}
                            alignItems={Align.Center}
                            width={40}
                            height={40}
                            rounding={0}
                            backgroundColor={colorSwatch.MissionRequirementsBG}
                        >
                            {mission.completed && (
                                <Image
                                    top={-15}
                                    height={63}
                                    width={48}
                                    src={UiTexturesManager.instance.check}
                                ></Image>
                            )}
                        </Panel>
                    </Panel>
                </Container>
                <Column
                    margin={15}
                    justifyContent={Justify.Center}
                    alignItems={Align.Center}
                >
                    <Container height={50} paddingTop={15}>
                        <Text fontSize={12} width={300} textAlign="center">
                            {insertNewlines(mission.instructions, 34)}
                        </Text>
                    </Container>
                    <Container height={50} justifyContent={Justify.Center}>
                        <ProgressBar
                            rounding={0}
                            bgColor={colorSwatch.RequirementsBarBG}
                            fgColor={colorSwatch.RequirementsBar}
                            value={mission.percentage}
                            width={304}
                            height={22}
                        ></ProgressBar>
                    </Container>
                    {!props.condense && (
                        <Container>
                            {!mission.completed ? (
                                <MenuButton
                                    fontSize={16}
                                    width={120}
                                    height={50}
                                    color={colorSwatch.SkipButton}
                                    onClick={() => {
                                        goToSkipMissions(props.missionId);
                                    }}
                                >
                                    {Localization.instance.get('Skip')}
                                </MenuButton>
                            ) : (
                                <Container width={120} height={50}></Container>
                            )}
                        </Container>
                    )}
                </Column>
            </Row>
        </Panel9Slice>
    );
};

export const MissionRequirements = () => {
    const rotbMats = useRotBMaterialContext() as RotBMaterialContextValue;

    const [allComplete] = useState(MissionManager.instance.getMissionsComplete());

    return (
        <Container
            position={PositionType.Absolute}
            width="100%"
            height="100%"
            flexDirection={FlexDirection.Row}
            justifyContent={Justify.Center}
        >
            <Column marginTop={36}>
                <Window>
                    <Column
                        height="100%"
                        marginTop={20}
                        alignSelf={Align.Center}
                        width={500}
                        rowGap={20}
                    >
                        <WindowHeader title={Localization.instance.get('Missions')} />
                        <Panel
                            width={500}
                            height={600}
                            rounding={0}
                            justifyContent={Justify.Center}
                            backgroundColor={colorSwatch.MalPortraitBackground}
                        >
                            <Column
                                flexGrow={1}
                                justifyContent={Justify.SpaceEvenly}
                                alignItems={Align.Center}
                            >
                                <MissionDetails missionId={0}></MissionDetails>
                                <MissionDetails missionId={1}></MissionDetails>
                                <MissionDetails missionId={2}></MissionDetails>
                            </Column>
                        </Panel>
                    </Column>
                </Window>
                <Row justifyContent={Justify.Center}>
                    {!allComplete ? (
                        <MenuButton
                            width={250}
                            height={54}
                            margin={6}
                            color={colorSwatch.AcceptButton}
                            fontSize={32}
                            sound={Sounds.cancel}
                            onClick={() => goToPreviousMenu(MenuPanels.MissionRequirements)}
                        >
                            {Localization.instance.get('PurchaseFailedDialogAccept')}
                        </MenuButton>
                    ) : (
                        <MenuButton
                            width={250}
                            height={54}
                            margin={6}
                            color={colorSwatch.SpendCoinsButton}
                            fontSize={32}
                            onClick={() => goToRewards()}
                        >
                            {Localization.instance.get('GetReward')}
                        </MenuButton>
                    )}
                </Row>
            </Column>
        </Container>
    );
};
